import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { automunityAPI } from '../lib/api';
import toast, { Toaster } from 'react-hot-toast';
import './Integrations.css';

interface ImportSource {
  source_id: number;
  name: string;
  provider: string;
  credential_key: string;
  base_url?: string;
  project_key?: string;
  import_filter?: string;
  status_map?: string;
  type_map?: string;
  priority_map?: string;
  assignee_map?: string;
  default_automation_id?: number;
  epic_as_automation?: number;
  is_active: number;
  last_import_at?: string;
}

interface ImportLog {
  log_id: number;
  source_id: number;
  run_at: string;
  external_id: string;
  external_type: string;
  action: string;
  automunity_id?: number;
  message?: string;
}

const PROVIDERS = [
  {
    id: 'jira',
    name: 'Jira',
    icon: '🟦',
    description: 'Import epics, stories, bugs, and tasks from Atlassian Jira',
    credentialFields: [
      { key: 'base_url', label: 'Jira URL', placeholder: 'https://yourcompany.atlassian.net', required: true },
      { key: 'email', label: 'Email', placeholder: 'you@company.com', required: true },
      { key: 'api_token', label: 'API Token', placeholder: 'Your Jira API token', required: true, secret: true },
    ],
    importPlaceholder: 'e.g., DEV-123'
  },
  {
    id: 'connectwise',
    name: 'ConnectWise Manage',
    icon: '🟧',
    description: 'Import service tickets and projects from ConnectWise Manage',
    credentialFields: [
      { key: 'base_url', label: 'API Base URL', placeholder: 'https://api-na.myconnectwise.net', required: true },
      { key: 'company_id', label: 'Company ID', placeholder: 'yourcompany', required: true },
      { key: 'public_key', label: 'Public Key', placeholder: 'Your CW public key', required: true },
      { key: 'private_key', label: 'Private Key', placeholder: 'Your CW private key', required: true, secret: true },
      { key: 'client_id', label: 'Client ID', placeholder: 'Your CW client ID (UUID)', required: true },
    ],
    importPlaceholder: 'e.g., 45678'
  }
];

const Integrations: React.FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const navigate = useNavigate();
  const [sources, setSources] = useState<ImportSource[]>([]);
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [credentialForm, setCredentialForm] = useState<Record<string, string>>({});
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [importId, setImportId] = useState('');
  const [bulkFilter, setBulkFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [deleteOnImport, setDeleteOnImport] = useState(false);
  const [activeTab, setActiveTab] = useState<'import' | 'mapping' | 'history' | 'settings'>('import');
  const [mappingData, setMappingData] = useState<{status_map: Record<string,string>, type_map: Record<string,string>, priority_map: Record<string,string>}>({status_map: {}, type_map: {}, priority_map: {}});
  const [newMappingRow, setNewMappingRow] = useState<{section: string, from: string, to: string}>({section: '', from: '', to: ''});

  const fetchSources = useCallback(async () => {
    try {
      const response = await automunityAPI.getImportSources();
      const result = await response.body.json() as any;
      let data: ImportSource[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') { try { data = JSON.parse(result.records); } catch { data = []; } }
      setSources(Array.isArray(data) ? data.filter((s: any) => s && s.source_id) : []);
    } catch (err) {
      console.error('Error fetching sources:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async (sourceId: number) => {
    try {
      const response = await automunityAPI.getImportLogs(sourceId);
      const result = await response.body.json() as any;
      let data: ImportLog[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') { try { data = JSON.parse(result.records); } catch { data = []; } }
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  // Load mapping data when sources are loaded
  useEffect(() => {
    if (sources.length > 0) {
      const source = sources.find(s => s.provider === provider);
      if (source) {
        const parseMap = (val: any) => {
          if (!val) return {};
          if (typeof val === 'string') { try { return JSON.parse(val); } catch { return {}; } }
          if (typeof val === 'object') return val;
          return {};
        };
        setMappingData({
          status_map: parseMap(source.status_map),
          type_map: parseMap(source.type_map),
          priority_map: parseMap(source.priority_map),
        });
      }
    }
  }, [sources, provider]);

  const handleSaveCredentials = async (providerId: string) => {
    const providerConfig = PROVIDERS.find(p => p.id === providerId);
    if (!providerConfig) return;

    // Validate required fields
    for (const field of providerConfig.credentialFields) {
      if (field.required && !credentialForm[field.key]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    try {
      await automunityAPI.createImportSource({
        name: `${providerConfig.name}`,
        provider: providerId,
        credentials: credentialForm,
        base_url: credentialForm.base_url || '',
        status_map: '{}',
        type_map: '{}',
        priority_map: '{}',
        assignee_map: '{}',
        is_active: 1
      });
      toast.success(`${providerConfig.name} credentials saved securely`);
      setShowCredentialModal(false);
      setCredentialForm({});
      fetchSources();
    } catch (err) {
      toast.error('Failed to save credentials');
    }
  };

  const handleImport = async (sourceId: number, externalId: string) => {
    if (!externalId.trim()) {
      toast.error('Enter a ticket or issue ID');
      return;
    }
    setImporting(true);
    try {
      const response = await automunityAPI.createImportSource({
        _action: 'invoke_import',
        source_id: sourceId,
        external_id: externalId.trim(),
        delete_on_import: deleteOnImport
      });
      const result = await response.body.json() as any;
      const parsed = result?.records ? (typeof result.records === 'string' ? JSON.parse(result.records) : result.records) : result;
      if (parsed?.error) {
        toast.error(parsed.error);
      } else {
        toast.success(parsed?.message || `Imported ${externalId}`);
        fetchLogs(sourceId);
      }
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
      setImportId('');
    }
  };

  const handleBulkImport = async (sourceId: number, filter: string) => {
    if (!filter.trim()) {
      toast.error('Enter a filter query');
      return;
    }
    setImporting(true);
    try {
      const response = await automunityAPI.createImportSource({
        _action: 'invoke_import',
        source_id: sourceId,
        import_filter: filter.trim()
      });
      const result = await response.body.json() as any;
      const parsed = result?.records ? (typeof result.records === 'string' ? JSON.parse(result.records) : result.records) : result;
      if (parsed?.error) {
        toast.error(parsed.error);
      } else {
        const r = parsed?.result || parsed;
        toast.success(`Import complete: ${r?.created || 0} created, ${r?.skipped || 0} skipped`);
        fetchLogs(sourceId);
      }
    } catch (err) {
      toast.error('Bulk import failed');
    } finally {
      setImporting(false);
      setBulkFilter('');
    }
  };

  // Provider detail page
  if (provider) {
    const providerConfig = PROVIDERS.find(p => p.id === provider);
    if (!providerConfig) {
      return <div style={{ padding: '30px' }}>Provider not found. <button onClick={() => navigate('/integrations')}>Back</button></div>;
    }

    const providerSource = sources.find(s => s.provider === provider);
    const isConnected = !!providerSource;

    // Load mapping data from source is handled in the useEffect below

    return (
      <div className="integrations-container">
        <Toaster position="top-right" />
        <div className="integrations-header">
          <button className="back-btn" onClick={() => navigate('/integrations')}>← Back</button>
          <h1>{providerConfig.icon} {providerConfig.name}</h1>
          <p>{providerConfig.description}</p>
        </div>

        {/* Connection status */}
        <div className="connection-status">
          <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '● Connected' : '○ Not Connected'}
          </span>
          <button className="btn btn-sm btn-primary" onClick={() => { setCredentialForm({}); setShowCredentialModal(true); }}>
            {isConnected ? 'Update Credentials' : 'Connect'}
          </button>
        </div>

        {isConnected && (
          <>
            {/* Tabs */}
            <div className="integration-tabs">
              <button className={activeTab === 'import' ? 'active' : ''} onClick={() => setActiveTab('import')}>
                Import
              </button>
              <button className={activeTab === 'mapping' ? 'active' : ''} onClick={() => setActiveTab('mapping')}>
                Mapping
              </button>
              <button className={activeTab === 'history' ? 'active' : ''} onClick={() => { setActiveTab('history'); fetchLogs(providerSource.source_id); }}>
                History
              </button>
              <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
                Settings
              </button>
            </div>

            {/* Import Tab */}
            {activeTab === 'import' && (
              <div className="import-section">
                <h3>Import a Ticket</h3>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  Enter a {provider === 'jira' ? 'Jira issue key' : 'ConnectWise ticket number'} to import it as a request in Automunity.
                </p>
                <div className="import-form">
                  <input
                    type="text"
                    className="form-control import-input"
                    placeholder={providerConfig.importPlaceholder}
                    value={importId}
                    onChange={e => setImportId(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && importId) handleImport(providerSource.source_id, importId); }}
                  />
                  <button
                    className="btn btn-success"
                    disabled={importing || !importId.trim()}
                    onClick={() => handleImport(providerSource.source_id, importId)}
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '13px', color: '#666' }}>
                  <input type="checkbox" checked={deleteOnImport} onChange={e => setDeleteOnImport(e.target.checked)} />
                  Delete from source on successful import
                </label>

                <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

                <h3>Bulk Import</h3>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  {provider === 'jira' 
                    ? 'Enter a JQL query to import multiple issues at once.' 
                    : 'Enter ConnectWise conditions to import multiple tickets at once.'}
                </p>
                <div className="import-form" style={{ flexDirection: 'column', gap: '10px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={provider === 'jira' 
                      ? 'e.g., project = DEV AND status != Done ORDER BY created DESC' 
                      : 'e.g., board/id = 1 AND status/name != "Closed"'}
                    value={bulkFilter}
                    onChange={e => setBulkFilter(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    disabled={importing || !bulkFilter.trim()}
                    onClick={() => handleBulkImport(providerSource.source_id, bulkFilter)}
                  >
                    {importing ? 'Importing...' : 'Run Bulk Import'}
                  </button>
                </div>
              </div>
            )}

            {/* Mapping Tab */}
            {activeTab === 'mapping' && (
              <MappingTab
                providerConfig={providerConfig}
                providerSource={providerSource}
                mappingData={mappingData}
                setMappingData={setMappingData}
              />
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="history-section">
                <h3>Import History</h3>
                {logs.length === 0 ? (
                  <p style={{ color: '#999' }}>No imports yet.</p>
                ) : (
                  <table className="import-log-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>External ID</th>
                        <th>Type</th>
                        <th>Action</th>
                        <th>Request</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.slice(0, 50).map(log => (
                        <tr key={log.log_id} className={`log-${log.action}`}>
                          <td>{new Date(log.run_at).toLocaleString()}</td>
                          <td className="mono">{log.external_id}</td>
                          <td>{log.external_type}</td>
                          <td><span className={`action-badge ${log.action}`}>{log.action.replace(/_/g, ' ')}</span></td>
                          <td>{log.automunity_id ? (
                            <a href={log.action === 'created_automation' ? `/automations/view/${log.automunity_id}` : `/requests/${log.automunity_id}`} target="_blank" rel="noreferrer">
                              #{log.automunity_id}
                            </a>
                          ) : '—'}</td>
                          <td>{log.message || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="settings-section">
                <h3>Import Settings</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Base URL</label>
                    <span>{providerSource.base_url || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Credential Key</label>
                    <span className="mono">{providerSource.credential_key}</span>
                  </div>
                  <div className="detail-item">
                    <label>Last Import</label>
                    <span>{providerSource.last_import_at ? new Date(providerSource.last_import_at).toLocaleString() : 'Never'}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Credential Modal */}
        {showCredentialModal && (
          <div className="modal-overlay" onClick={() => setShowCredentialModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>{isConnected ? 'Update' : 'Connect'} {providerConfig.name}</h3>
              {providerConfig.credentialFields.map(field => (
                <div className="form-group" key={field.key}>
                  <label>{field.label} {field.required && '*'}</label>
                  <input
                    type={field.secret ? 'password' : 'text'}
                    className="form-control"
                    placeholder={field.placeholder || ''}
                    value={credentialForm[field.key] || ''}
                    onChange={e => setCredentialForm({...credentialForm, [field.key]: e.target.value})}
                  />
                </div>
              ))}
              <p className="credential-help">Credentials are encrypted and stored securely in AWS Parameter Store.</p>
              <div className="modal-actions">
                <button className="btn btn-success" onClick={() => handleSaveCredentials(provider)}>
                  {isConnected ? 'Update' : 'Connect'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowCredentialModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main page — provider tiles
  if (loading) return <div style={{ padding: '30px' }}>Loading...</div>;

  return (
    <div className="integrations-container">
      <Toaster position="top-right" />
      <div className="integrations-header">
        <h1>🔌 Integrations</h1>
        <p>Connect external systems to import requests and automations into Automunity</p>
      </div>

      <div className="provider-tiles">
        {PROVIDERS.map(p => {
          const isConnected = sources.some(s => s.provider === p.id);
          return (
            <div key={p.id} className="provider-tile" onClick={() => navigate(`/integrations/${p.id}`)}>
              <div className="provider-tile-icon">{p.icon}</div>
              <h3>{p.name}</h3>
              <p>{p.description}</p>
              <span className={`connection-badge ${isConnected ? 'connected' : ''}`}>
                {isConnected ? '● Connected' : 'Not connected'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Mapping Tab Component
interface MappingTabProps {
  providerConfig: typeof PROVIDERS[0];
  providerSource: ImportSource;
  mappingData: {status_map: Record<string,string>, type_map: Record<string,string>, priority_map: Record<string,string>};
  setMappingData: (data: any) => void;
}

const MappingTab: React.FC<MappingTabProps> = ({ providerConfig, providerSource, mappingData, setMappingData }) => {
  const [sourceMetadata, setSourceMetadata] = useState<{statuses: string[], issue_types: string[]}>({statuses: [], issue_types: []});
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [newMappingRow, setNewMappingRow] = useState<{section: string, from: string, to: string}>({section: '', from: '', to: ''});

  useEffect(() => {
    const fetchMetadata = async () => {
      setLoadingMeta(true);
      try {
        const response = await automunityAPI.createImportSource({
          _action: 'get_metadata',
          source_id: providerSource.source_id
        });
        const result = await response.body.json() as any;
        const parsed = result?.records ? (typeof result.records === 'string' ? JSON.parse(result.records) : result.records) : result;
        if (parsed?.statuses || parsed?.issue_types) {
          setSourceMetadata({
            statuses: parsed.statuses || [],
            issue_types: parsed.issue_types || []
          });
        }
      } catch (err) {
        console.error('Error fetching metadata:', err);
      } finally {
        setLoadingMeta(false);
      }
    };
    fetchMetadata();
  }, [providerSource.source_id]);

  const sections = [
    { key: 'status_map' as const, label: 'Status', sourceOptions: sourceMetadata.statuses, 
      automunityOptions: ['open', 'in_progress', 'waiting_on_information', 'backlogged', 'completed', 'completed_in_review', 'pending_deployment', 'cancelled', 'closed_in_production', 'closed_work_completed'] },
    { key: 'type_map' as const, label: 'Type', sourceOptions: sourceMetadata.issue_types,
      automunityOptions: ['feature', 'bug', 'improvement', 'question', 'administrative'] },
    { key: 'priority_map' as const, label: 'Priority', sourceOptions: [],
      automunityOptions: ['critical', 'high', 'medium', 'low'] },
  ];

  return (
    <div className="mapping-section-tab">
      <h3>Field Mapping</h3>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Map values from {providerConfig.name} to Automunity. When importing, these mappings translate statuses, types, and priorities automatically.
        {loadingMeta && <span style={{ marginLeft: '10px', color: '#007bff' }}>Loading source values...</span>}
      </p>

      {sections.map(({ key, label, sourceOptions, automunityOptions }) => {
        const currentMap = mappingData[key] || {};
        return (
          <div key={key} style={{ marginBottom: '24px' }}>
            <h4 style={{ marginBottom: '8px' }}>{label} Mapping</h4>
            <table className="import-log-table" style={{ marginBottom: '8px' }}>
              <thead>
                <tr>
                  <th>{providerConfig.name} Value</th>
                  <th>→</th>
                  <th>Automunity Value</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(currentMap).map(([from, to]) => (
                  <tr key={from}>
                    <td>{from}</td>
                    <td style={{ textAlign: 'center', color: '#999' }}>→</td>
                    <td>{to}</td>
                    <td>
                      <button className="btn btn-sm btn-danger" onClick={() => {
                        const updated = { ...currentMap };
                        delete updated[from];
                        setMappingData({ ...mappingData, [key]: updated });
                      }}>×</button>
                    </td>
                  </tr>
                ))}
                {Object.keys(currentMap).length === 0 && (
                  <tr><td colSpan={4} style={{ color: '#999', textAlign: 'center' }}>No mappings yet</td></tr>
                )}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {sourceOptions.length > 0 ? (
                <select
                  className="form-control"
                  style={{ flex: 1 }}
                  value={newMappingRow.section === key ? newMappingRow.from : ''}
                  onChange={e => setNewMappingRow({ section: key, from: e.target.value, to: newMappingRow.section === key ? newMappingRow.to : '' })}
                >
                  <option value="">Select {providerConfig.name} {label.toLowerCase()}...</option>
                  {sourceOptions.filter(o => !currentMap[o]).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  placeholder={`${providerConfig.name} value...`}
                  style={{ flex: 1 }}
                  value={newMappingRow.section === key ? newMappingRow.from : ''}
                  onChange={e => setNewMappingRow({ section: key, from: e.target.value, to: newMappingRow.section === key ? newMappingRow.to : '' })}
                />
              )}
              <span style={{ color: '#999' }}>→</span>
              <select
                className="form-control"
                style={{ flex: 1 }}
                value={newMappingRow.section === key ? newMappingRow.to : ''}
                onChange={e => setNewMappingRow({ ...newMappingRow, section: key, to: e.target.value })}
              >
                <option value="">Select Automunity {label.toLowerCase()}...</option>
                {automunityOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <button className="btn btn-sm btn-success" onClick={() => {
                if (newMappingRow.from && newMappingRow.to && newMappingRow.section === key) {
                  setMappingData({ ...mappingData, [key]: { ...currentMap, [newMappingRow.from]: newMappingRow.to } });
                  setNewMappingRow({ section: '', from: '', to: '' });
                }
              }}>Add</button>
            </div>
          </div>
        );
      })}

      <button className="btn btn-primary" style={{ marginTop: '10px' }} onClick={async () => {
        try {
          await automunityAPI.createImportSource({
            _action: 'update_mappings',
            source_id: providerSource.source_id,
            status_map: JSON.stringify(mappingData.status_map),
            type_map: JSON.stringify(mappingData.type_map),
            priority_map: JSON.stringify(mappingData.priority_map),
          });
          toast.success('Mappings saved');
        } catch (err) {
          toast.error('Failed to save mappings');
        }
      }}>
        Save Mappings
      </button>
    </div>
  );
};

export default Integrations;
